import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Alert,
  RadioGroup,
  Radio,
  IconButton,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import {
  CalendarMonth,
  Refresh,
  Delete,
  Add,
  Groups,
  InfoOutlined,
  Check,
  Close,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  CustomButton,
  CustomButtonWithBorder,
} from "components/CustomComponents/CustomButton";
import { customAPI, customGetAPI, customDeleteAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { infoStyle, toAlignEnd, themes } from "services/constants";
import { cardStyle } from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";

const calculationBasisOptions = [
  {
    id: "actual",
    label: "Dynamic Calendar Days",
    recommended: true,
    description: "Uses actual calendar days in each payroll period.",
    formula: "Formula: Daily Rate = Salary ÷ Calendar Days",
    note: "Calendar days vary by period (28 / 29 / 30 / 31), based on the actual days in the payroll month.",
    iconColor: "#16a34a",
    formulaBg: "#eafaf1",
    formulaBorder: "#b7e4c7",
    formulaText: "#1b7a4d",
    chipBg: "#dcfce7",
  },
  {
    id: "fixed",
    label: "Fixed (Based on Payroll Schedule)",
    description:
      "Uses fixed day(s) based on payroll schedule(s) configured below.",
    formula: "Formula: Daily Rate = Salary ÷ Fixed Days (as per payroll schedule)",
    iconColor: "#16a34a",
    formulaBg: "#eafaf1",
    formulaBorder: "#b7e4c7",
    formulaText: "#1b7a4d",
    chipBg: "#dcfce7",
  },
];

const sectionCardStyle = {
  p: 3,
  mb: 3,
  borderRadius: 2,
  border: `1px solid ${themes?.miliedLightGray}`,
  backgroundColor: themes?.whiteColor,
};

const stepBadgeStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: themes?.greenColor,
  color: themes?.whiteColor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
  flexShrink: 0,
};

// Switching between the Settings sidebar tabs fully unmounts this component,
// so its mount effect would otherwise refetch these settings every time the
// user revisits this tab even though nothing changed. Caching the last
// fetched values at module scope (reset only by a full page reload) avoids
// that — a fetch only happens on the first-ever visit this session, or after
// a save/restore actually changes the data.
let cachedDailyRateData = null;

const SETTINGS_ENDPOINT = "payroll/setup/dailyratecalculation/daily-rate-settings/";
const SCHEDULES_ENDPOINT = "payroll/setup/dailyratecalculation/daily-rate-schedules/";

const CALCULATION_METHOD_TO_FORM = { dynamic: "actual", fixed: "fixed" };
const FORM_TO_CALCULATION_METHOD = { actual: "dynamic", fixed: "fixed" };

const payrollScheduleDefs = [
  { label: "Weekly", defaultDays: 5, description: "One payroll run every week." },
  {
    label: "Bi-Weekly",
    defaultDays: 10,
    description: "One payroll run every two weeks.",
  },
  {
    label: "Semi-Monthly",
    defaultDays: 13,
    description: "Two payroll runs every month.",
  },
  {
    label: "Monthly",
    defaultDays: 26,
    description: "One payroll run every month.",
  },
];

const dailyRateBasisRows = [
  {
    key: "paid",
    label: "Weekly Off is Paid",
    note: "Weekly offs are paid and included in payroll days.",
    formula: "Daily Rate = Salary ÷ Calendar Days (e.g., 31)",
    example: "Example: ₹3,100 ÷ 31 = ₹100.00",
  },
  {
    key: "unpaid",
    label: "Weekly Off is Unpaid",
    note: "Weekly offs are unpaid and excluded from payroll days.",
    formula: "Daily Rate = Salary ÷ (Calendar Days − Weekly Off Days)",
    example: "Example: ₹3,100 ÷ (31 − 3) = ₹110.71",
  },
];

export const DailyCalculationBasis = () => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(false);
  const [settingsId, setSettingsId] = useState(null);
  const [savedCalculationBasis, setSavedCalculationBasis] = useState("actual");
  const [scheduleRows, setScheduleRows] = useState([]);
  const [originalScheduleRows, setOriginalScheduleRows] = useState([]);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [pendingType, setPendingType] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    watch,
  } = useForm({
    mode: "onBlur",
    defaultValues: { calculation_basis: "actual" },
  });

  const fetchData = async () => {
    const [settingsRes, schedulesRes] = await Promise.all([
      customGetAPI(`${domain}${SETTINGS_ENDPOINT}`),
      customGetAPI(`${domain}${SCHEDULES_ENDPOINT}`),
    ]);

    let nextSettingsId = settingsId;
    let nextCalculationBasis = watch("calculation_basis");
    let nextScheduleRows = scheduleRows;
    let nextOriginalScheduleRows = originalScheduleRows;

    if (settingsRes?.ok && settingsRes.responseDetails) {
      const payload = settingsRes.responseDetails;
      const data = Array.isArray(payload)
        ? payload[0]
        : Array.isArray(payload?.results)
        ? payload.results[0]
        : payload;
      nextSettingsId = data?.id ?? null;
      nextCalculationBasis =
        CALCULATION_METHOD_TO_FORM[data?.calculation_method] || "actual";
      setSettingsId(nextSettingsId);
      setSavedCalculationBasis(nextCalculationBasis);
      reset({ calculation_basis: nextCalculationBasis });
    }

    if (schedulesRes?.ok && schedulesRes.responseDetails) {
      const payload = schedulesRes.responseDetails;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
        ? payload.results
        : [];
      nextScheduleRows = list.map((row) => ({
        id: row.id,
        payroll_schedule: row.payroll_schedule,
        description: row.description,
        fixed_days: row.fixed_days,
        is_default: !!row.is_default,
        is_active: !!row.is_active,
      }));
      nextOriginalScheduleRows = nextScheduleRows;
      setScheduleRows(nextScheduleRows);
      setOriginalScheduleRows(nextOriginalScheduleRows);
    }

    cachedDailyRateData = {
      settingsId: nextSettingsId,
      calculationBasis: nextCalculationBasis,
      scheduleRows: nextScheduleRows,
      originalScheduleRows: nextOriginalScheduleRows,
    };
  };

  useEffect(() => {
    if (cachedDailyRateData) {
      setSettingsId(cachedDailyRateData.settingsId);
      setSavedCalculationBasis(cachedDailyRateData.calculationBasis);
      reset({ calculation_basis: cachedDailyRateData.calculationBasis });
      setScheduleRows(cachedDailyRateData.scheduleRows);
      setOriginalScheduleRows(cachedDailyRateData.originalScheduleRows);
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const onSubmit = async (data) => {
    setLoading(true);

    // Fixed-day payroll schedules are saved first, and the calculation
    // method second — Fixed mode's calculation method depends on those
    // schedules already existing, so the order matters.
    const removedIds = originalScheduleRows
      .map((row) => row.id)
      .filter((id) => !scheduleRows.some((row) => row.id === id));

    const scheduleUpsertRequests = scheduleRows.map((row) => {
      const payload = {
        payroll_schedule: row.payroll_schedule,
        description: row.description,
        fixed_days: Number(row.fixed_days),
        is_default: row.is_default,
        is_active: row.is_active,
      };
      return row.id
        ? customAPI(`${domain}${SCHEDULES_ENDPOINT}${row.id}/`, "PUT", payload)
        : customAPI(`${domain}${SCHEDULES_ENDPOINT}`, "POST", payload);
    });

    const scheduleDeleteRequests = removedIds.map((id) =>
      customDeleteAPI(`${domain}${SCHEDULES_ENDPOINT}${id}/`)
    );

    const scheduleResponses = await Promise.all([
      ...scheduleUpsertRequests,
      ...scheduleDeleteRequests,
    ]);

    const failedScheduleResponse = scheduleResponses.find(
      (res) => !res || !res.ok
    );
    if (failedScheduleResponse) {
      setLoading(false);
      showToast(
        "error",
        failedScheduleResponse?.message || "Failed to save payroll schedules."
      );
      return;
    }

    // Only touch the calculation-method API when it actually toggled between
    // Dynamic and Fixed — saving with it unchanged shouldn't call the API.
    const calculationBasisChanged = data.calculation_basis !== savedCalculationBasis;
    let settingsResponse = null;
    if (calculationBasisChanged) {
      const settingsPayload = {
        calculation_method:
          FORM_TO_CALCULATION_METHOD[data.calculation_basis] || "dynamic",
      };
      settingsResponse = settingsId
        ? await customAPI(
            `${domain}${SETTINGS_ENDPOINT}${settingsId}/`,
            "PUT",
            settingsPayload
          )
        : await customAPI(`${domain}${SETTINGS_ENDPOINT}`, "POST", settingsPayload);
    }

    setLoading(false);

    if (settingsResponse && !settingsResponse.ok) {
      showToast(
        "error",
        settingsResponse?.message ||
          "Failed to save daily rate calculation settings."
      );
      return;
    }

    if (calculationBasisChanged) {
      setSavedCalculationBasis(data.calculation_basis);
    }
    showToast("success", "Daily rate calculation settings saved successfully.");
    fetchData();
  };

  const handleRestoreDefaults = async () => {
    reset({ calculation_basis: "actual" });

    // Already Dynamic — nothing to restore, so don't call the API.
    if (savedCalculationBasis === "actual") {
      showToast(
        "success",
        "Daily rate calculation method restored to Dynamic Calendar Days."
      );
      return;
    }

    setLoading(true);
    const settingsPayload = { calculation_method: "dynamic" };
    const response = settingsId
      ? await customAPI(
          `${domain}${SETTINGS_ENDPOINT}${settingsId}/`,
          "PUT",
          settingsPayload
        )
      : await customAPI(`${domain}${SETTINGS_ENDPOINT}`, "POST", settingsPayload);
    setLoading(false);

    if (!response?.ok) {
      showToast(
        "error",
        response?.message || "Failed to restore default settings."
      );
      return;
    }

    if (!settingsId) {
      setSettingsId(response.responseDetails?.id ?? null);
    }
    setSavedCalculationBasis("actual");
    showToast(
      "success",
      "Daily rate calculation method restored to Dynamic Calendar Days."
    );
  };

  const selectedBasis = watch("calculation_basis");
  const isFixed = selectedBasis === "fixed";
  const activeOption =
    calculationBasisOptions.find((opt) => opt.id === selectedBasis) ||
    calculationBasisOptions[0];
  const accentColor = activeOption.iconColor;
  const accentChipBg = activeOption.chipBg;

  // The Save button only makes sense once something has actually changed —
  // either the calculation method itself, or a schedule row was added,
  // removed, or edited (fixed days / active status) relative to what's
  // currently saved.
  const isFormDirty = useMemo(() => {
    if (selectedBasis !== savedCalculationBasis) return true;
    if (scheduleRows.length !== originalScheduleRows.length) return true;
    return scheduleRows.some((row) => {
      const original = originalScheduleRows.find((o) => o.id === row.id);
      if (!original) return true;
      return (
        Number(row.fixed_days) !== Number(original.fixed_days) ||
        !!row.is_active !== !!original.is_active
      );
    });
  }, [selectedBasis, savedCalculationBasis, scheduleRows, originalScheduleRows]);

  const availableTypeOptions = payrollScheduleDefs.filter(
    (f) => !scheduleRows.some((row) => row.payroll_schedule === f.label)
  );

  const handleConfirmAddScheduleRow = () => {
    const def = payrollScheduleDefs.find((f) => f.label === pendingType);
    if (!def) return;
    setScheduleRows((prev) => [
      ...prev,
      {
        id: null,
        payroll_schedule: def.label,
        description: def.description,
        fixed_days: def.defaultDays,
        is_default: false,
        is_active: true,
      },
    ]);
    setIsAddingRow(false);
    setPendingType("");
  };

  const handleCancelAddScheduleRow = () => {
    setIsAddingRow(false);
    setPendingType("");
  };

  const handleUpdateScheduleRow = (payrollSchedule, patch) => {
    setScheduleRows((prev) =>
      prev.map((row) =>
        row.payroll_schedule === payrollSchedule ? { ...row, ...patch } : row
      )
    );
  };

  const handleRemoveScheduleRow = (payrollSchedule) => {
    setScheduleRows((prev) =>
      prev.filter((row) => row.payroll_schedule !== payrollSchedule)
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Daily Rate Calculation
      </Typography>
      <Typography {...infoStyle} sx={{ mb: 2 }}>
        Define how daily rate is calculated from salary and how payable days
        are determined.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Changes affect future payroll calculations only. Previously
        processed payroll will not be recalculated automatically.
      </Alert>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper elevation={0} sx={sectionCardStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box sx={{ ...stepBadgeStyle, backgroundColor: accentColor }}>1</Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Choose Daily Rate Calculation Method
            </Typography>
          </Box>

          <Controller
            name="calculation_basis"
            control={control}
            rules={{ required: "Calculation basis is required" }}
            render={({ field }) => (
              <RadioGroup
                row
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
              >
                {calculationBasisOptions.map((opt) => {
                  const isActive = field.value === opt.id;
                  return (
                    <Box
                      key={opt.id}
                      sx={{
                        ...cardStyle(isActive),
                        flex: 1,
                        minWidth: 280,
                        p: 2,
                        border: `1px solid ${
                          isActive ? opt.iconColor : themes?.miliedLightGray
                        }`,
                        "&:hover": { borderColor: opt.iconColor },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Radio
                          value={opt.id}
                          sx={{
                            "&.Mui-checked": { color: opt.iconColor },
                          }}
                        />
                        <Typography
                          fontWeight="medium"
                          sx={{
                            color: isActive ? opt.iconColor : "inherit",
                          }}
                        >
                          {opt.label}
                        </Typography>
                        {opt.recommended && (
                          <Chip
                            label="Recommended"
                            size="small"
                            sx={{
                              backgroundColor: "#dcfce7",
                              color: "#16a34a",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ marginLeft: "32px", mb: 1.5 }}
                      >
                        {opt.description}
                      </Typography>
                      <Box
                        sx={{
                          marginLeft: "32px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: opt.iconColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarMonth
                            sx={{ color: themes?.whiteColor }}
                            fontSize="small"
                          />
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: opt.formulaBg,
                            border: `1px solid ${opt.formulaBorder}`,
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: opt.formulaText, fontWeight: 600 }}
                          >
                            {opt.formula}
                          </Typography>
                        </Box>
                      </Box>
                      {opt.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", marginLeft: "32px", mt: 1 }}
                        >
                          {opt.note}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </RadioGroup>
            )}
          />
        </Paper>

        {isFixed && (
          <Paper elevation={0} sx={sectionCardStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ ...stepBadgeStyle, backgroundColor: accentColor }}>2</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Configure Fixed Days by Payroll Schedule
              </Typography>
              <Chip
                label="On-Demand"
                size="small"
                sx={{ backgroundColor: accentChipBg, color: accentColor, fontWeight: 600 }}
              />
            </Box>
            <Typography {...infoStyle} sx={{ mb: 2, ml: 4 }}>
              Add the payroll schedules used in your organisation and define
              the fixed days for each schedule.
            </Typography>

            <TableContainer
              sx={{ border: `1px solid ${themes?.miliedLightGray}`, borderRadius: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: themes?.primary }}>
                    <TableCell sx={{ fontWeight: 700 }}>Payroll Schedule</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Description (Optional)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Fixed Days (Divisor)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduleRows.map((row) => (
                    <TableRow key={row.payroll_schedule}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {row.payroll_schedule}
                          </Typography>
                          {row.is_default && (
                            <Chip
                              label="Default"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography color="text.secondary">
                          {row.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={row.fixed_days}
                          onChange={(event) => {
                            const rawValue = event.target.value;
                            const clampedValue =
                              rawValue === ""
                                ? ""
                                : Math.min(31, Math.max(0, Number(rawValue)));
                            handleUpdateScheduleRow(row.payroll_schedule, {
                              fixed_days: clampedValue,
                            });
                          }}
                          inputProps={{ min: 0, max: 31, step: 1, style: { width: 60 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row.is_active}
                          onChange={(event) =>
                            handleUpdateScheduleRow(row.payroll_schedule, {
                              is_active: event.target.checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          disabled={row.is_default}
                          onClick={() => handleRemoveScheduleRow(row.payroll_schedule)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {isAddingRow ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  border: `1px dashed ${themes?.miliedLightGray}`,
                  width: "fit-content",
                }}
              >
                <Select
                  size="small"
                  displayEmpty
                  value={pendingType}
                  onChange={(event) => setPendingType(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="" disabled>
                    Select payroll schedule
                  </MenuItem>
                  {availableTypeOptions.map((f) => (
                    <MenuItem key={f.label} value={f.label}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
                <IconButton
                  size="small"
                  disabled={!pendingType}
                  onClick={handleConfirmAddScheduleRow}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancelAddScheduleRow}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              availableTypeOptions.length > 0 && (
                <CustomButtonWithBorder
                  actionName="Add Payroll Schedule"
                  typeName="button"
                  AddIcon={<Add fontSize="small" />}
                  actionFuntion={() => setIsAddingRow(true)}
                  customTitleSx={{
                    mt: 2,
                    border: `1.5px solid ${accentColor}`,
                    color: accentColor,
                    "&:hover": {
                      backgroundColor: accentChipBg,
                      borderColor: accentColor,
                    },
                  }}
                />
              )
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Daily rate will be calculated using the fixed days configured
              for the payroll schedule of each pay run.
            </Alert>
          </Paper>
        )}

        {!isFixed && (
          <Paper elevation={0} sx={sectionCardStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ ...stepBadgeStyle, backgroundColor: accentColor }}>2</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                How Daily Rate is Calculated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (Based on Weekly Off Setting in Shift Category)
              </Typography>
              <Tooltip title="This reference table explains how the Weekly Off Paid/Unpaid setting affects the Dynamic Calendar Days calculation.">
                <InfoOutlined fontSize="small" sx={{ color: themes?.grayColor }} />
              </Tooltip>
            </Box>
            <Typography {...infoStyle} sx={{ mb: 2, ml: 4 }}>
              The rules below are derived from the Weekly Off Paid/Unpaid
              settings configured in each employee's Shift Category.
            </Typography>

            <TableContainer
              sx={{ border: `1px solid ${themes?.miliedLightGray}`, borderRadius: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: themes?.primary }}>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Weekly Off Setting in Shift Category
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      How Daily Rate is Calculated (Dynamic)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyRateBasisRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              backgroundColor: "#dcfce7",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Groups sx={{ color: "#16a34a" }} fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 600 }}>{row.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.note}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: "#16a34a" }}>
                          {row.formula}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.example}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              Values shown above are for reference only. Actual calculation
              will be based on the employee's assigned Shift Category.
            </Alert>
          </Paper>
        )}

        <Box sx={{ ...toAlignEnd, mt: 3 }}>
          <CustomButtonWithBorder
            actionName="Restore Default Settings"
            typeName="button"
            AddIcon={<Refresh fontSize="small" />}
            actionFuntion={handleRestoreDefaults}
            disableAction={loading}
            customTitleSx={{
              border: `1.5px solid ${accentColor}`,
              color: accentColor,
              "&:hover": {
                backgroundColor: accentChipBg,
                borderColor: accentColor,
              },
            }}
          />
          <CustomButton
            actionName="Save Changes"
            typeName="submit"
            disableAction={loading || !isFormDirty}
            loading={loading}
            customTitleSx={{
              background: accentColor,
              color: themes?.whiteColor,
              "&:hover": { backgroundColor: accentColor },
            }}
          />
        </Box>
      </form>
    </Box>
  );
};
